import { useState, useEffect } from "react";

// 🎯 TIPOS PARA LAZY LOADING
export interface ImageLoadOptions {
  placeholder?: string;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  width?: number;
  height?: number;
  blur?: number;
}

export interface LazyImageConfig {
  quality: any;
  threshold: number;
  rootMargin: string;
  enableBlur: boolean;
  enableWebP: boolean;
  cacheSize: number;
}

// 🚀 IMAGE LAZY LOAD SERVICE
export class ImageLazyLoadService {
  private imageCache = new Map<string, string>();
  private observer: IntersectionObserver | null = null;
  private config: LazyImageConfig;
  constructor(config: Partial<LazyImageConfig> = {}) {
    this.config = {
      quality: 80,
      threshold: 0.1,
      rootMargin: '50px',
      enableBlur: true,
      enableWebP: true,
      cacheSize: 100,
      ...config,
    };

    this.initIntersectionObserver();
  }

  // 🎯 INICIALIZAR INTERSECTION OBSERVER
  private initIntersectionObserver(): void {
    if (typeof window === 'undefined') return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            this.loadImage(img);
            this.observer?.unobserve(img);
          }
        });
      },
      {
        threshold: this.config.threshold,
        rootMargin: this.config.rootMargin,
      }
    );
  }

  // 🎯 OBSERVAR IMAGEN PARA LAZY LOADING
  observeImage(img: HTMLImageElement): void {
    if (this.observer) {
      this.observer.observe(img);
    }
  }

  // 🎯 CARGAR IMAGEN
  private async loadImage(img: HTMLImageElement): Promise<void> {
    const src = img.dataset.src;
    if (!src) return;

    try {
      // Verificar cache primero
      if (this.imageCache.has(src)) {
        img.src = this.imageCache.get(src)!;
        img.classList.remove('lazy-loading');
        img.classList.add('lazy-loaded');
        return;
      }

      // Cargar imagen
      await this.loadImageWithFallback(img, src);
      
      // Cachear imagen
      this.cacheImage(src, img.src);
      
    } catch (error) {
      console.error('Error loading image:', error);
      this.handleImageError(img);
    }
  }

  // 🎯 CARGAR IMAGEN CON FALLBACK
  private async loadImageWithFallback(img: HTMLImageElement, src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tempImg = new Image();
      
      tempImg.onload = () => {
        img.src = tempImg.src;
        img.classList.remove('lazy-loading');
        img.classList.add('lazy-loaded');
        resolve();
      };

      tempImg.onerror = () => {
        reject(new Error(`Failed to load image: ${src}`));
      };

      // Optimizar URL si es posible
      const optimizedSrc = this.optimizeImageUrl(src);
      tempImg.src = optimizedSrc;
    });
  }

  // 🎯 OPTIMIZAR URL DE IMAGEN
  private optimizeImageUrl(src: string): string {
    // Si ya es una URL optimizada, retornar tal como está
    if (src.includes('?') || src.startsWith('data:')) {
      return src;
    }

    // Aquí puedes agregar lógica para optimizar URLs
    // Por ejemplo, agregar parámetros de calidad, formato, etc.
    
    // Ejemplo: agregar parámetros de optimización
    const url = new URL(src, window.location.origin);
    
    if (this.config.enableWebP) {
      url.searchParams.set('format', 'webp');
    }
    
    if (this.config.quality) {
      url.searchParams.set('quality', this.config.quality.toString());
    }

    return url.toString();
  }

  // 🎯 CACHEAR IMAGEN
  private cacheImage(key: string, value: string): void {
    // Verificar límite de cache
    if (this.imageCache.size >= this.config.cacheSize) {
      const firstKey = this.imageCache.keys().next().value;
      if (firstKey) {
        this.imageCache.delete(firstKey);
      }
    }

    this.imageCache.set(key, value);
  }

  // 🎯 MANEJAR ERROR DE IMAGEN
  private handleImageError(img: HTMLImageElement): void {
    img.classList.remove('lazy-loading');
    img.classList.add('lazy-error');
    
    // Establecer imagen de placeholder
    if (img.dataset.placeholder) {
      img.src = img.dataset.placeholder;
    } else {
      // Imagen de error por defecto
      img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgMTEwQzEwNS41MjMgMTEwIDExMCAxMDUuNTIzIDExMCAxMDBDMTEwIDk0LjQ3NzIgMTA1LjUyMyA5MCAxMDAgOTBDOTQuNDc3MiA5MCA5MCA5NC40NzcyIDkwIDEwMEM5MCAxMDUuNTIzIDk0LjQ3NzIgMTEwIDEwMCAxMTBaIiBmaWxsPSIjOTNBQkM1Ii8+CjxwYXRoIGQ9Ik0xMDAgMTMwQzEwNS41MjMgMTMwIDExMCAxMjUuNTIzIDExMCAxMjBDMTEwIDExNC40NzcgMTA1LjUyMyAxMTAgMTAwIDExMEM5NC40NzcyIDExMCA5MCAxMTQuNDc3IDkwIDEyMEM5MCAxMjUuNTIzIDk0LjQ3NzIgMTMwIDEwMCAxMzBaIiBmaWxsPSIjOTNBQkM1Ii8+Cjwvc3ZnPgo=';
    }
  }

  // 🎯 LIMPIAR CACHE
  clearCache(): void {
    this.imageCache.clear();
  }

  // 🎯 DESTRUIR OBSERVER
  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// 🎯 INSTANCIA GLOBAL DEL IMAGE LAZY LOAD SERVICE
export const imageLazyLoad = new ImageLazyLoadService({
  threshold: 0.1,
  rootMargin: '50px',
  enableBlur: true,
  enableWebP: true,
  cacheSize: 50,
});

// 🎯 HOOK PARA LAZY LOADING
export function useLazyImage(src: string, options: ImageLoadOptions = {}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    
    img.onload = () => {
      setImageSrc(img.src);
      setIsLoaded(true);
      setIsError(false);
    };

    img.onerror = () => {
      setIsError(true);
      setIsLoaded(false);
      setImageSrc(options.placeholder || '');
    };

    // Optimizar URL si es posible
    const optimizedSrc = imageLazyLoad['optimizeImageUrl'](src);
    img.src = optimizedSrc;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, options.placeholder]);

  return {
    isLoaded,
    isError,
    imageSrc,
  };
} 
